import { get, intersection } from 'lodash';
import { QuantityCalculator } from '../quantity-calculator/quantityCalculator';
import { ProductInventoryStatus, IProductPreOrderAvailability, } from '../types/product';
export function last(array) {
    return array[array.length - 1];
}
export function first(array) {
    return array[0];
}
export function selectProductOptionItems(product) {
    return product.options;
}
export function selectProductOptionItemsByType(product, type) {
    return product.options.find(function (o) { return o.optionType === type; });
}
export function inStock(product, variant) {
    var ref = variant || product;
    if (product.isInStock === false) {
        return false;
    }
    if (!variant && product.isInStock !== undefined) {
        return product.isInStock;
    }
    if (product.isTrackingInventory) {
        if (variant) {
            return ref.inventory.quantity > 0;
        }
        return product.inventory.quantity > 0 || product.productItems.some(function (item) { return item.inventory.quantity > 0; });
    }
    return ref.inventory.status === ProductInventoryStatus.IN_STOCK;
}
export function isPreOrder(product, variant) {
    if (!product && !variant) {
        return false;
    }
    if (variant) {
        return variant.availableForPreOrder;
    }
    if (product.isManageProductItems) {
        var isAllVariantsPreOrder = product.productItemsPreOrderAvailability === IProductPreOrderAvailability.ALL_VARIANTS;
        var isSomeVariantsPreOrder = product.productItemsPreOrderAvailability === IProductPreOrderAvailability.SOME_VARIANTS;
        return isAllVariantsPreOrder || (isSomeVariantsPreOrder && !product.isInStock);
    }
    return product.inventory.availableForPreOrder;
}
export function userInputsFactory() {
    return { selection: [], text: [], quantity: [], subscriptionPlan: [] };
}
export function validateUserInputs(product, userInputs, isValidateInventory) {
    if (isValidateInventory === void 0) { isValidateInventory = true; }
    var errors = userInputsFactory();
    var quantityRange = QuantityCalculator.getQuantitiesRange(product, userInputs.selection);
    var userQuantity = Number(userInputs.quantity[0]);
    product.options.forEach(function (_, i) {
        errors.selection[i] = !userInputs.selection[i];
    });
    if (product.customTextFields) {
        product.customTextFields.forEach(function (item, i) {
            errors.text[i] = item.isMandatory === true && !userInputs.text[i];
        });
    }
    errors.quantity = [
        isValidateInventory
            ? (isNaN(userQuantity) || userQuantity < first(quantityRange) || userQuantity > last(quantityRange)) === true
            : false,
    ];
    var isProductSubscription = !!get(product.subscriptionPlans, ['list', '0']);
    var isSelectedPlan = !!get(userInputs.subscriptionPlan, ['0']);
    errors.subscriptionPlan = [isProductSubscription && !isSelectedPlan];
    return errors;
}
export function formatCustomTextFields(product, userInputs) {
    var customTextFields = [];
    return (product.customTextFields || []).reduce(function (acc, field, i) {
        acc.push({
            customText: { title: field.title, key: field.key, isMandatory: field.isMandatory },
            answer: userInputs.text[i] || '',
        });
        return acc;
    }, customTextFields);
}
export function hasVariantInStock(product) {
    return product.productItems.some(function (option) { return inStock(product, option); });
}
export function hasSubscriptionPlans(product) {
    var _a;
    return ((_a = product.subscriptionPlans) === null || _a === void 0 ? void 0 : _a.list.filter(function (subPlan) { return subPlan.visible === true; }).length) > 0;
}
export function isSelectedSubscriptionOneTimePurchase(selectedSubscriptionPlanId) {
    return selectedSubscriptionPlanId === 'one-time-purchase';
}
export function isSubscribeNow(product, selectedSubscriptionPlanId) {
    return !isSelectedSubscriptionOneTimePurchase(selectedSubscriptionPlanId) && hasSubscriptionPlans(product);
}
export function actualPrice(product, variant) {
    var isDiscounted = hasDiscount(product);
    var isSurcharged = hasSurcharge(variant);
    var ref = variant || product;
    if (isSurcharged) {
        return isDiscounted ? variant.comparePrice : variant.price;
    }
    return isDiscounted ? ref.comparePrice : ref.price;
}
export function hasAutomaticDiscount(product) {
    var _a;
    return Boolean((_a = product.itemDiscount) === null || _a === void 0 ? void 0 : _a.priceAfterDiscount);
}
export function hasDiscount(product) {
    var _a;
    return ((_a = product.discount) === null || _a === void 0 ? void 0 : _a.value) > 0;
}
function hasManualDiscount(product, variant) {
    var _a, _b;
    if (variant) {
        return (_a = variant.hasDiscount) !== null && _a !== void 0 ? _a : (Boolean(variant.formattedComparePrice) && variant.comparePrice > 0);
    }
    else {
        return ((_b = product.discount) === null || _b === void 0 ? void 0 : _b.value) > 0 && Boolean(product.formattedComparePrice);
    }
}
export function hasAnyDiscount(product, variant) {
    return hasManualDiscount(product, variant) || hasAutomaticDiscount(product);
}
export function formattedPrimaryPrice(product, variant) {
    var isAutomaticDiscountExist = hasAutomaticDiscount(product);
    var isManualDiscountExist = hasManualDiscount(product, variant);
    if (isAutomaticDiscountExist) {
        return product.itemDiscount.priceAfterDiscount;
    }
    else if (isManualDiscountExist) {
        return variant ? variant.formattedComparePrice : product.formattedComparePrice;
    }
    return product.formattedPrice;
}
export function formattedSecondaryPrice(product, variant) {
    var isAutomaticDiscountExist = hasAutomaticDiscount(product);
    var isManualDiscountExist = hasManualDiscount(product, variant);
    if (isAutomaticDiscountExist && isManualDiscountExist) {
        return product.formattedComparePrice;
    }
    else if (isManualDiscountExist || isAutomaticDiscountExist) {
        if (isManualDiscountExist) {
            return variant ? variant.formattedPrice : product.formattedPrice;
        }
        return product.formattedPrice;
    }
    return undefined;
}
export function hasSurcharge(variant) {
    return (variant === null || variant === void 0 ? void 0 : variant.surcharge) < 0;
}
export function actualSku(product, variant) {
    return (variant === null || variant === void 0 ? void 0 : variant.sku) || product.sku;
}
export function variantsCount(product, _a) {
    var _b, _c, _d, _e;
    var _f = _a === void 0 ? {} : _a, _g = _f.includeModifiers, includeModifiers = _g === void 0 ? true : _g;
    if (includeModifiers) {
        if (((_b = product.productItemsSummary) === null || _b === void 0 ? void 0 : _b.productItemsCount) > 0) {
            return product.productItemsSummary.productItemsCount;
        }
        else if (((_c = product.options) === null || _c === void 0 ? void 0 : _c.length) > 0) {
            return product.options.map(function (o) { return o.selections.length; }).reduce(function (prev, current) { return prev * current; }, 1);
        }
        return 0;
    }
    else {
        return (_e = (_d = product.productItemsSummary) === null || _d === void 0 ? void 0 : _d.productItemsCount) !== null && _e !== void 0 ? _e : 0;
    }
}
export function getOptionBySelectionId(_a) {
    var product = _a.product, selectionId = _a.selectionId;
    return product.options.find(function (option) { return option.selections.some(function (selection) { return selection.id === selectionId; }); });
}
export function findProductItemBySelectionIds(_a) {
    var product = _a.product, selectionIds = _a.selectionIds;
    return product.productItems.find(function (productItem) {
        return productItem.optionsSelections.every(function (selectionIdInItem) { return selectionIds.includes(selectionIdInItem); });
    });
}
export function getProductOptionsSelectionIdsWithoutModifiers(product) {
    return getProductOptionsWithoutModifiers(product)
        .map(function (option) { return option.selections.map(function (selection) { return selection.id; }); })
        .flat();
}
export function getProductOptionsWithoutModifiers(product) {
    var _a, _b, _c;
    var aVariantOptionsSelection = (_b = (_a = product.productItems) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.optionsSelections;
    return (_c = product.options) === null || _c === void 0 ? void 0 : _c.filter(function (option) {
        var _a;
        return intersection((_a = option.selections) === null || _a === void 0 ? void 0 : _a.map(function (selection) { return selection.id; }), aVariantOptionsSelection).length > 0;
    });
}
export function getModifiersIndexes(product) {
    var _a, _b, _c;
    var aVariantOptionsSelection = (_b = (_a = product.productItems) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.optionsSelections;
    var modifierIndexes = [];
    (_c = product.options) === null || _c === void 0 ? void 0 : _c.forEach(function (option, index) {
        var _a;
        if (intersection((_a = option.selections) === null || _a === void 0 ? void 0 : _a.map(function (selection) { return selection.id; }), aVariantOptionsSelection).length === 0) {
            modifierIndexes.push(index);
        }
    });
    return modifierIndexes;
}
//# sourceMappingURL=productUtils.js.map